
import { useState } from "react";
import { base44 } from "@/api/base44Client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, CheckCircle, Save, RotateCcw } from "lucide-react";

export default function ConsequenceSettings() {
  const [message, setMessage] = useState(null);
  const queryClient = useQueryClient();

  const { data: consequenceLevels = [], isLoading } = useQuery({
    queryKey: ['consequenceLevels'],
    queryFn: () => base44.entities.ConsequenceLevel.list('sort_order'),
  });

  const [editedLevels, setEditedLevels] = useState({});

  const updateLevelMutation = useMutation({
    mutationFn: async (levelData) => {
      return base44.entities.ConsequenceLevel.update(levelData.id, {
        level_name: levelData.level_name,
        level_description: levelData.level_description,
        refusal_threshold: levelData.refusal_threshold,
        badge_emoji: levelData.badge_emoji,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['consequenceLevels']);
      setMessage({ type: 'success', text: 'Consequence settings updated successfully!' });
      setEditedLevels({});
      setTimeout(() => setMessage(null), 3000);
    },
    onError: (err) => {
      setMessage({ type: 'error', text: err.message || 'Failed to update settings' });
      setTimeout(() => setMessage(null), 5000);
    }
  });

  const initializeDefaultsMutation = useMutation({
    mutationFn: async () => {
      // Check if defaults already exist
      const existing = await base44.entities.ConsequenceLevel.list();
      if (existing.length > 0) {
        throw new Error('Consequence levels already exist. Use Reset to Defaults instead.');
      }

      // Create default consequence levels
      const defaults = [
        {
          level_key: 'warning',
          level_name: 'Warning',
          level_description: 'Conference with student, document conversation + 1 makeup day',
          refusal_threshold: 1,
          badge_emoji: '⚠️',
          badge_color: 'bg-green-100 text-green-800',
          sort_order: 1
        },
        {
          level_key: 'parent_contact',
          level_name: 'Parent Contact',
          level_description: 'Phone call to parent/guardian required + 2 makeup days',
          refusal_threshold: 2,
          badge_emoji: '📞',
          badge_color: 'bg-yellow-100 text-yellow-800',
          sort_order: 2
        },
        {
          level_key: 'detention',
          level_name: 'Detention',
          level_description: 'Assign detention, notify parent + 3 makeup days',
          refusal_threshold: 3,
          badge_emoji: '📋',
          badge_color: 'bg-orange-100 text-orange-800',
          sort_order: 3
        },
        {
          level_key: 'iss',
          level_name: 'ISS',
          level_description: 'In-School Suspension recommendation + 4 makeup days',
          refusal_threshold: 4,
          badge_emoji: '🚫',
          badge_color: 'bg-red-100 text-red-800',
          sort_order: 4
        },
        {
          level_key: 'admin_review',
          level_name: 'Admin Review',
          level_description: 'Escalate to admin, OSS consideration + 5+ makeup days',
          refusal_threshold: 5,
          badge_emoji: '⛔',
          badge_color: 'bg-red-900 text-white',
          sort_order: 5
        }
      ];

      for (const level of defaults) {
        await base44.entities.ConsequenceLevel.create(level);
      }

      return defaults.length;
    },
    onSuccess: (count) => {
      queryClient.invalidateQueries(['consequenceLevels']);
      setMessage({ type: 'success', text: `Created ${count} default consequence levels!` });
      setTimeout(() => setMessage(null), 3000);
    },
    onError: (err) => {
      setMessage({ type: 'error', text: err.message || 'Failed to initialize defaults' });
      setTimeout(() => setMessage(null), 5000);
    }
  });

  const resetToDefaultsMutation = useMutation({
    mutationFn: async () => {
      // Delete all existing levels
      for (const level of consequenceLevels) {
        await base44.entities.ConsequenceLevel.delete(level.id);
      }

      // Create defaults
      const defaults = [
        {
          level_key: 'warning',
          level_name: 'Warning',
          level_description: 'Conference with student, document conversation + 1 makeup day',
          refusal_threshold: 1,
          badge_emoji: '⚠️',
          badge_color: 'bg-green-100 text-green-800',
          sort_order: 1
        },
        {
          level_key: 'parent_contact',
          level_name: 'Parent Contact',
          level_description: 'Phone call to parent/guardian required + 2 makeup days',
          refusal_threshold: 2,
          badge_emoji: '📞',
          badge_color: 'bg-yellow-100 text-yellow-800',
          sort_order: 2
        },
        {
          level_key: 'detention',
          level_name: 'Detention',
          level_description: 'Assign detention, notify parent + 3 makeup days',
          refusal_threshold: 3,
          badge_emoji: '📋',
          badge_color: 'bg-orange-100 text-orange-800',
          sort_order: 3
        },
        {
          level_key: 'iss',
          level_name: 'ISS',
          level_description: 'In-School Suspension recommendation + 4 makeup days',
          refusal_threshold: 4,
          badge_emoji: '🚫',
          badge_color: 'bg-red-100 text-red-800',
          sort_order: 4
        },
        {
          level_key: 'admin_review',
          level_name: 'Admin Review',
          level_description: 'Escalate to admin, OSS consideration + 5+ makeup days',
          refusal_threshold: 5,
          badge_emoji: '⛔',
          badge_color: 'bg-red-900 text-white',
          sort_order: 5
        }
      ];

      for (const level of defaults) {
        await base44.entities.ConsequenceLevel.create(level);
      }

      return defaults.length;
    },
    onSuccess: (count) => {
      queryClient.invalidateQueries(['consequenceLevels']);
      setMessage({ type: 'success', text: `Reset to ${count} default consequence levels!` });
      setTimeout(() => setMessage(null), 3000);
    },
    onError: (err) => {
      setMessage({ type: 'error', text: err.message || 'Failed to reset defaults' });
      setTimeout(() => setMessage(null), 5000);
    }
  });

  const handleChange = (levelId, field, value) => {
    setEditedLevels(prev => ({
      ...prev,
      [levelId]: {
        ...(prev[levelId] || consequenceLevels.find(l => l.id === levelId)),
        [field]: value
      }
    }));
  };

  const handleSave = (levelId) => {
    const levelData = editedLevels[levelId];
    if (levelData) {
      updateLevelMutation.mutate(levelData);
    }
  };

  const getLevelData = (level) => {
    return editedLevels[level.id] || level;
  };

  if (isLoading) {
    return (
      <div className="p-4 md:p-8 bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen">
        <div className="max-w-5xl mx-auto">
          <p className="text-center text-slate-500">Loading...</p>
        </div>
      </div>
    );
  }

  if (consequenceLevels.length === 0) {
    return (
      <div className="p-4 md:p-8 bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen">
        <div className="max-w-5xl mx-auto space-y-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-orange-700" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900">Consequence Settings</h1>
              <p className="text-slate-600 mt-1">Configure EA Consequence Levels and Thresholds</p>
            </div>
          </div>

          <Card className="border-none shadow-lg">
            <CardContent className="p-8 text-center">
              <AlertTriangle className="w-16 h-16 text-orange-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-900 mb-2">No Consequence Levels Found</h3>
              <p className="text-slate-600 mb-6">Initialize default consequence levels to get started.</p>
              <Button 
                onClick={() => initializeDefaultsMutation.mutate()}
                className="bg-red-700 hover:bg-red-800"
                disabled={initializeDefaultsMutation.isPending}
              >
                {initializeDefaultsMutation.isPending ? 'Creating...' : 'Initialize Defaults'}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen">
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-orange-700" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900">Consequence Settings</h1>
              <p className="text-slate-600 mt-1">Configure EA Consequence Levels and Thresholds</p>
            </div>
          </div>
          <Button 
            onClick={() => resetToDefaultsMutation.mutate()}
            variant="outline"
            className="border-slate-300"
            disabled={resetToDefaultsMutation.isPending}
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            {resetToDefaultsMutation.isPending ? 'Resetting...' : 'Reset to Defaults'}
          </Button>
        </div>

        {message && (
          <Alert className={message.type === 'success' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}>
            {message.type === 'success' ? (
              <CheckCircle className="w-4 h-4 text-green-600" />
            ) : (
              <AlertTriangle className="w-4 h-4 text-red-600" />
            )}
            <AlertDescription className={message.type === 'success' ? 'text-green-900' : 'text-red-900'}>
              {message.text}
            </AlertDescription>
          </Alert>
        )}

        <Alert className="bg-blue-50 border-blue-200">
          <AlertTriangle className="w-4 h-4 text-blue-700" />
          <AlertDescription className="text-blue-900">
            <strong>Important:</strong> Changes to consequence settings will apply to future syncs. Re-sync EA misses after making changes to update existing student records.
          </AlertDescription>
        </Alert>

        <div className="space-y-4">
          {consequenceLevels.map((level) => {
            const currentData = getLevelData(level);
            const hasChanges = !!editedLevels[level.id];

            return (
              <Card key={level.id} className="border-none shadow-lg">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Badge className={level.badge_color}>
                        {currentData.badge_emoji} {currentData.level_name}
                      </Badge>
                      <span className="text-sm text-slate-600">
                        Triggers at <strong>{currentData.refusal_threshold}</strong> refusal{currentData.refusal_threshold > 1 ? 's' : ''}
                      </span>
                    </div>
                    {hasChanges && (
                      <Button 
                        onClick={() => handleSave(level.id)}
                        size="sm"
                        className="bg-green-600 hover:bg-green-700"
                        disabled={updateLevelMutation.isPending}
                      >
                        <Save className="w-4 h-4 mr-2" />
                        Save Changes
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor={`name-${level.id}`}>Level Name</Label>
                      <Input
                        id={`name-${level.id}`}
                        value={currentData.level_name}
                        onChange={(e) => handleChange(level.id, 'level_name', e.target.value)}
                        placeholder="e.g., Warning, Detention"
                      />
                    </div>
                    <div>
                      <Label htmlFor={`threshold-${level.id}`}>Refusal Threshold</Label>
                      <Input
                        id={`threshold-${level.id}`}
                        type="number"
                        min="1"
                        value={currentData.refusal_threshold}
                        onChange={(e) => handleChange(level.id, 'refusal_threshold', parseInt(e.target.value) || 1)}
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        Number of refusals needed to trigger this level
                      </p>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor={`emoji-${level.id}`}>Badge Emoji</Label>
                    <Input
                      id={`emoji-${level.id}`}
                      value={currentData.badge_emoji}
                      onChange={(e) => handleChange(level.id, 'badge_emoji', e.target.value)}
                      placeholder="⚠️"
                      className="w-24"
                    />
                  </div>

                  <div>
                    <Label htmlFor={`description-${level.id}`}>Action Description</Label>
                    <Textarea
                      id={`description-${level.id}`}
                      value={currentData.level_description}
                      onChange={(e) => handleChange(level.id, 'level_description', e.target.value)}
                      placeholder="What action should be taken at this level?"
                      className="h-20"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      This will be displayed to APs as guidance for the intervention
                    </p>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <Card className="border-none shadow-lg bg-slate-50">
          <CardContent className="p-6">
            <h3 className="font-semibold text-slate-900 mb-3">Tips for Setting Consequence Levels:</h3>
            <ul className="space-y-2 text-sm text-slate-700 list-disc list-inside">
              <li><strong>Thresholds:</strong> Set refusal counts that make sense for your school's discipline policy</li>
              <li><strong>Progressive:</strong> Each level should escalate in severity</li>
              <li><strong>Clear Actions:</strong> Describe exactly what APs should do at each level</li>
              <li><strong>Makeup Days:</strong> System automatically sets makeup days = refusal count</li>
              <li><strong>After Changes:</strong> Re-sync EA misses to apply new consequence levels to existing students</li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
